import { useState, useEffect } from "react";
import { MASTERDATA_SERVICE, restApiRequest } from "src/utils/Api";
import { dynamicNotification } from "src/utils/Notifications";
import { mockData } from "src/Pages/Supplier/List/table";
import __ from "src/utils/Translations";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";

export default function useProductSuppliers(
  asOption = false,
  idToLowercase = false,
  ids,
  noFetching = false
) {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (noFetching) {
      setData([]);
      return;
    }

    const fetch = async () => {
      const { totalItems } =
        (await restApiRequest(
          MASTERDATA_SERVICE,
          "/supplierobject/nontouristic",
          "GET",
          {
            params: {
              rowsPerPage: 1,
            },
          },
          { member: mockData }
        )) || {};

      const batches = Array.from(
        { length: Math.ceil(totalItems / 3000) },
        (_, i) => i + 1
      );

      try {
        const res = await Promise.all(
          batches.map((batch) =>
            restApiRequest(
              MASTERDATA_SERVICE,
              "/supplierobject/nontouristic",
              "GET",
              {
                params: {
                  rowsPerPage: 3000,
                  page: batch,
                },
              },
              { member: mockData }
            )
          )
        );

        const items = [];
        res?.forEach(({ member }) => items.push(...member));

        let result = items.filter(
          ({ supplierObjectBusinessID }) => supplierObjectBusinessID
        );
        if (idToLowercase) {
          result = result.map((item) => ({
            ...item,
            supplierObjectBusinessID:
              item.supplierObjectBusinessID.toLowerCase(),
          }));
        }

        if (asOption) {
          result = result.map(({ supplierObjectBusinessID, objectName }) => ({
            value: supplierObjectBusinessID,
            label: objectName,
          }));
        }
        setData(result);
      } catch (e) {
        newRelicErrorReport(
          e,
          "utils/hooks/suppliers/useProductSuppliers.js - 48"
        );
        dynamicNotification(
          e.message || __("Nie udało się pobrać listy dostawców"),
          "error"
        );
      }
    };

    const fetchWithIds = async () => {
      try {
        const chunkSize = 100; // endpoint "/supplierobject/nontouristic" has limitation of ids number (100 ids per request)
        const idChunks = [];

        for (let i = 0; i < ids.length; i += chunkSize) {
          idChunks.push(ids.slice(i, i + chunkSize));
        }

        const fetchChunk = async (idChunk) => {
          const resData = await restApiRequest(
            MASTERDATA_SERVICE,
            "/supplierobject/nontouristic",
            "GET",
            {
              params: {
                supplierObjectBusinessID: idChunk.join(","),
              },
            },
            { member: mockData }
          );
          return resData.member;
        };

        const allResults = (await Promise.all(idChunks.map(fetchChunk))).flat();

        let result = allResults.filter(
          ({ supplierObjectBusinessID }) => supplierObjectBusinessID
        );

        if (idToLowercase) {
          result = result.map((item) => ({
            ...item,
            supplierObjectBusinessID:
              item.supplierObjectBusinessID.toLowerCase(),
          }));
        }

        if (asOption) {
          result = result.map(({ supplierObjectBusinessID, objectName }) => ({
            value: supplierObjectBusinessID,
            label: objectName,
          }));
        }

        setData(result);
      } catch (e) {
        newRelicErrorReport(
          e,
          "utils/hooks/suppliers/useProductSuppliers.js - 141"
        );
        dynamicNotification(
          e.message || __("Nie udało się pobrać listy dostawców"),
          "error"
        );
      }
    };

    if (ids?.length) {
      fetchWithIds();
    } else {
      fetch();
    }
  }, [asOption, idToLowercase, noFetching, ids]);

  return data;
}
